/**
 * TeamDashboardModal
 *
 * 팀 플랜 사용자를 위한 팀 대시보드(요약) 모달
 */

import { useEffect, useMemo, useState } from 'react';
import { useBodyScrollLock } from '../hooks';
import { useLanguage, parseUTCDate } from '../i18n';
import { getTeamDashboard, getTeamHistory, type TeamDashboardResponse, type TeamHistoryItem, type TeamHistoryResponse } from '../api';

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

function StatusPill({ status }: { status: string | null }) {
  const key = (status || '').toLowerCase();
  const map: Record<string, { label: string; cls: string }> = {
    go: { label: 'GO', cls: 'bg-status-go text-white' },
    conditional: { label: 'COND', cls: 'bg-status-warning text-white' },
    holding: { label: 'HOLD', cls: 'bg-status-holding text-white' },
    no_go: { label: 'NO', cls: 'bg-status-danger text-white' },
  };
  const v = map[key] || map.no_go;
  return (
    <span className={`${v.cls} text-[10px] font-bold px-2 py-0.5 rounded`} data-testid={`team-status-${v.label}`}>
      {v.label}
    </span>
  );
}

export default function TeamDashboardModal({ isOpen, onClose }: Props) {
  const { lang, t } = useLanguage();
  const [data, setData] = useState<TeamHistoryResponse | null>(null);
  const [dashboard, setDashboard] = useState<TeamDashboardResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useBodyScrollLock(isOpen);

  useEffect(() => {
    if (!isOpen) return;
    setIsLoading(true);
    Promise.all([
      getTeamHistory(1, 10),
      getTeamDashboard(30),
    ])
      .then(([historyResp, dashboardResp]) => {
        setData(historyResp);
        setDashboard(dashboardResp);
      })
      .catch(() => {
        setData(null);
        setDashboard(null);
      })
      .finally(() => setIsLoading(false));
  }, [isOpen]);

  const items: TeamHistoryItem[] = useMemo(() => data?.items || [], [data]);
  const status = dashboard?.status_distribution;
  const topUsers = dashboard?.top_users || [];
  const daily = useMemo(() => {
    const all = dashboard?.daily_evaluations || [];
    // 최근 14일만 표시
    return all.slice(Math.max(0, all.length - 14));
  }, [dashboard]);
  const maxDaily = useMemo(() => Math.max(1, ...daily.map((d) => d.count)), [daily]);
  
  // 이번 주 평가 건수 계산 (최근 7일)
  const thisWeekCount = useMemo(() => {
    const all = dashboard?.daily_evaluations || [];
    const last7Days = all.slice(Math.max(0, all.length - 7));
    return last7Days.reduce((sum, d) => sum + d.count, 0);
  }, [dashboard]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid="team-dashboard-modal-overlay"
    >
      <div
        className="neo-card-static bg-terminal-bg border border-terminal-border rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="team-dashboard-modal"
      >
        <div className="sticky top-0 bg-terminal-bg border-b border-terminal-border px-6 py-4 flex items-center justify-between z-10">
          <div>
            <h2 className="text-lg font-black text-terminal-text">
              {t.dashboard.teamDashboardTitle || (lang === 'ko' ? '팀 대시보드' : 'Team Dashboard')}
            </h2>
            <p className="text-xs text-terminal-muted mt-1">
              {data?.team_name ? (lang === 'ko' ? `팀: ${data.team_name}` : `Team: ${data.team_name}`) : (lang === 'ko' ? '팀 전체 평가 현황' : 'Team evaluation overview')}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-terminal-dim/30 rounded-lg transition-colors"
            aria-label={lang === 'ko' ? '닫기' : 'Close'}
            data-testid="team-dashboard-close"
          >
            <svg className="w-5 h-5 text-terminal-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-terminal-dim/20 border border-terminal-border rounded-lg p-4">
              <p className="text-xs text-terminal-muted mb-1">{lang === 'ko' ? '총 평가(팀)' : 'Total (team)'}</p>
              <p className="text-2xl font-black text-terminal-text tabular-nums" data-testid="team-dashboard-total">
                {data?.total ?? 0}
              </p>
            </div>
            <div className="bg-terminal-dim/20 border border-terminal-border rounded-lg p-4">
              <p className="text-xs text-terminal-muted mb-1">{lang === 'ko' ? '이번 주 평가' : 'This Week'}</p>
              <p className="text-2xl font-black text-terminal-text tabular-nums" data-testid="team-dashboard-this-week">
                {thisWeekCount}
              </p>
            </div>
          </div>

          {/* 상태별 통계 (한 줄) */}
          <div className="neo-card-static bg-white dark:bg-[#1e2a3a] p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-black text-black dark:text-white uppercase">
                {lang === 'ko' ? '상태별 통계' : 'Status Breakdown'}
              </h3>
              {dashboard?.period_days ? (
                <span className="text-[11px] text-terminal-muted">
                  {lang === 'ko' ? `최근 ${dashboard.period_days}일` : `Last ${dashboard.period_days} days`}
                </span>
              ) : null}
            </div>
            <div className="grid grid-cols-4 gap-2 text-center">
              <div className="bg-black/5 dark:bg-white/5 rounded-lg p-3">
                <div className="text-[10px] text-terminal-muted mb-1">GO</div>
                <div className="text-lg font-black text-status-go tabular-nums">{status?.go ?? 0}</div>
              </div>
              <div className="bg-black/5 dark:bg-white/5 rounded-lg p-3">
                <div className="text-[10px] text-terminal-muted mb-1">COND</div>
                <div className="text-lg font-black text-status-warning tabular-nums">{status?.conditional ?? 0}</div>
              </div>
              <div className="bg-black/5 dark:bg-white/5 rounded-lg p-3">
                <div className="text-[10px] text-terminal-muted mb-1">HOLD</div>
                <div className="text-lg font-black text-status-holding tabular-nums">{status?.holding ?? 0}</div>
              </div>
              <div className="bg-black/5 dark:bg-white/5 rounded-lg p-3">
                <div className="text-[10px] text-terminal-muted mb-1">NO</div>
                <div className="text-lg font-black text-status-danger tabular-nums">{status?.no_go ?? 0}</div>
              </div>
            </div>
          </div>

          {/* 사용자별 통계 Top 5 (한 줄) */}
          <div className="neo-card-static bg-white dark:bg-[#1e2a3a] p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-black text-black dark:text-white uppercase">
                {lang === 'ko' ? '사용자별 통계 (Top 5)' : 'By User (Top 5)'}
              </h3>
            </div>
            {topUsers.length > 0 ? (
              <div className="grid grid-cols-5 gap-2">
                {topUsers.map((u) => {
                  const label = (u.user_name || u.user_email || '').split('@')[0] || '—';
                  return (
                    <div key={u.user_id} className="bg-black/5 dark:bg-white/5 rounded-lg p-3 text-center">
                      <div className="text-[11px] font-bold text-terminal-text truncate" title={u.user_email}>
                        {label}
                      </div>
                      <div className="text-xs text-terminal-muted mt-1 tabular-nums">
                        {lang === 'ko' ? `${u.count}건` : `${u.count}`}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-terminal-muted text-center py-4">
                {lang === 'ko' ? '표시할 데이터가 없어요.' : 'No data to show.'}
              </p>
            )}
          </div>

          {/* 날짜별 생성 수 (그래프) */}
          <div className="neo-card-static bg-white dark:bg-[#1e2a3a] p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-black text-black dark:text-white uppercase">
                {lang === 'ko' ? '날짜별 생성 수' : 'Daily Created'}
              </h3>
              <span className="text-[11px] text-terminal-muted">
                {lang === 'ko' ? '최근 14일' : 'Last 14 days'}
              </span>
            </div>
            {daily.length > 0 ? (
              <div className="h-24 flex items-end gap-1">
                {daily.map((d) => {
                  const h = Math.round((d.count / maxDaily) * 100);
                  const mmdd = d.date.slice(5).replace('-', '/');
                  return (
                    <div key={d.date} className="flex-1 flex flex-col items-center gap-1">
                      <div
                        className="w-full bg-status-go/70 rounded-sm"
                        style={{ height: `${Math.max(2, h)}%` }}
                        title={`${d.date}: ${d.count}`}
                      />
                      <div className="text-[9px] text-terminal-muted whitespace-nowrap">{mmdd}</div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-terminal-muted text-center py-4">
                {lang === 'ko' ? '표시할 데이터가 없어요.' : 'No data to show.'}
              </p>
            )}
          </div>

          <div className="neo-card-static bg-white dark:bg-[#1e2a3a] p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-black text-black dark:text-white uppercase">
                {lang === 'ko' ? '최근 팀 활동' : 'Recent Team Activity'}
              </h3>
            </div>

            {isLoading ? (
              <div className="space-y-2">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-10 bg-black/5 dark:bg-white/5 animate-pulse rounded" />
                ))}
              </div>
            ) : items.length > 0 ? (
              <div className="space-y-2">
                {items.map((it) => (
                  <div
                    key={it.id}
                    className="flex items-center justify-between p-3 bg-black/5 dark:bg-white/5 rounded-lg"
                    data-testid={`team-history-item-${it.id}`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <StatusPill status={it.status} />
                      <div className="min-w-0">
                        <p className="text-sm text-terminal-text truncate">{it.feature_name}</p>
                        <p className="text-[11px] text-terminal-muted truncate">
                          {it.user_name || it.user_email}
                        </p>
                      </div>
                    </div>
                    <span className="text-xs text-terminal-muted whitespace-nowrap">
                      {parseUTCDate(it.created_at).toLocaleDateString(lang === 'ko' ? 'ko-KR' : 'en-US', { month: 'short', day: 'numeric' })}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-terminal-muted text-center py-6" data-testid="team-dashboard-empty">
                {lang === 'ko' ? '팀 평가 기록이 아직 없어요.' : 'No team evaluations yet.'}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}


